/**
 * The after-workflow question.
 *
 * Asked once, after a person has actually used a view for a while — not on
 * every login, not as a modal, not with stars. Two answers. "Worked well"
 * is thanked and dismissed; "Had friction" opens the same drawer as the
 * Feedback door. Ask only when the answer can improve the product.
 *
 * Dismissal is remembered for the browser session, per view, so nobody is
 * asked twice in a sitting.
 */
import React, { useEffect, useState } from 'react';
import './feedback.css';

const DEFAULT_DELAY_MS = 60_000;

function seen(key: string): boolean {
  try { return window.sessionStorage.getItem(key) === '1'; } catch { return false; }
}
function remember(key: string) {
  try { window.sessionStorage.setItem(key, '1'); } catch { /* private mode; ask again next time */ }
}

export default function WorkflowPulse({
  storageKey, delayMs = DEFAULT_DELAY_MS, onFriction,
}: {
  storageKey: string;
  delayMs?: number;
  onFriction: () => void;
}) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    setShow(false);
    if (seen(storageKey)) return undefined;
    const t = window.setTimeout(() => setShow(true), delayMs);
    return () => window.clearTimeout(t);
  }, [storageKey, delayMs]);

  if (!show) return null;

  const done = () => { remember(storageKey); setShow(false); };

  return (
    <div className="fb-root">
      <div className="fb-pulse" role="group" aria-label="How did this workflow go?">
        <span>How did this workflow go?</span>
        <button type="button" onClick={done}>Worked well</button>
        <button type="button" onClick={() => { done(); onFriction(); }}>Had friction</button>
      </div>
    </div>
  );
}
